import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Inbox, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import EnquiriesView from "@/components/admin/EnquiriesView";
import ApplicationsView from "@/components/admin/ApplicationsView";

const SECTIONS = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "applications", label: "Applications", icon: FileText },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("enquiries");

  const onUnauthorized = () => navigate("/admin/login", { replace: true });

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex" data-testid="admin-dashboard-page" style={{ backgroundColor: "#F9F6F0" }}>
      <aside
        className="hidden md:flex md:w-64 flex-col"
        style={{ backgroundColor: "#0A1128", color: "#F9F6F0", borderRight: "1px solid #E2DCD0" }}
      >
        <div className="p-8 flex items-center gap-3 border-b" style={{ borderColor: "rgba(249,246,240,0.12)" }}>
          <span
            className="inline-flex items-center justify-center"
            style={{ width: 32, height: 32, border: "1px solid #C5A059", color: "#C5A059" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v18" />
              <path d="M5 21h14" />
              <path d="M5 7h14" />
              <path d="M5 7l-2 6a4 4 0 0 0 8 0L9 7" />
              <path d="M19 7l-2 6a4 4 0 0 0 8 0l-2-6" />
            </svg>
          </span>
          <span className="font-serif-display text-base" style={{ letterSpacing: "0.06em" }}>Sikdar &amp; Sikdar · Admin</span>
        </div>

        <nav className="flex-1 p-6 space-y-1">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = section === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setSection(s.key)}
                data-testid={`sidebar-${s.key}`}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors"
                style={{
                  letterSpacing: "0.08em",
                  color: "#F9F6F0",
                  backgroundColor: active ? "rgba(197,160,89,0.12)" : "transparent",
                  borderLeft: active ? "2px solid #C5A059" : "2px solid transparent",
                }}
              >
                <Icon size={16} strokeWidth={1.6} /> {s.label}
              </button>
            );
          })}
        </nav>

        <div className="p-6 border-t" style={{ borderColor: "rgba(249,246,240,0.12)" }}>
          <div className="text-xs" style={{ color: "rgba(249,246,240,0.55)", letterSpacing: "0.16em", textTransform: "uppercase" }}>
            Signed in as
          </div>
          <div className="mt-1 text-sm break-all">{user?.email}</div>
          <button onClick={doLogout} className="btn-ghost-light mt-5 w-full justify-center" data-testid="admin-logout">
            <LogOut size={14} strokeWidth={1.6} /> Logout
          </button>
          <Link
            to="/"
            className="block mt-3 text-center text-[11px] uppercase"
            style={{ color: "rgba(249,246,240,0.55)", letterSpacing: "0.2em" }}
            data-testid="admin-view-site"
          >
            View public site
          </Link>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile section switcher */}
        <div className="md:hidden flex gap-1 px-4 pt-4">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSection(s.key)}
              className="flex-1 px-4 py-3 text-xs uppercase"
              data-testid={`mobile-sidebar-${s.key}`}
              style={{
                letterSpacing: "0.16em",
                backgroundColor: section === s.key ? "#0A1128" : "#FFFFFF",
                color: section === s.key ? "#F9F6F0" : "#0A1128",
                border: "1px solid #E2DCD0",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="container-x py-10">
          {section === "enquiries" ? (
            <EnquiriesView onUnauthorized={onUnauthorized} />
          ) : (
            <ApplicationsView onUnauthorized={onUnauthorized} />
          )}
        </div>
      </main>
    </div>
  );
}
